/**
 * MAILBOX-UNIT account manager — data layer (2026-09-19 operatör isteği).
 *
 * The 11 mailbox-unit providers (rambler, aol, web.de, libero, tiscali.it,
 * tiscali.cz, vip.sina.com, sina.com, gazeta.pl, rediffmail, t-online.de) are
 * NOT a parent/alias family: each mailbox is ONE sellable unit and the scarce
 * resource is the per-(mailbox, site) sale right. This module is the fetch layer
 * for the shared <MailboxUnitManager> card that all 11 pages adopt.
 *
 * ONE read model feeds the table: GET /admin/providers/:id/site-usage already
 * returns units{}, probe{}, sites[] and mailboxes[]; the manager only needs the
 * mailboxes[] rows (now carrying accountId, aliasId and a derived leaseState).
 *
 * Every mutation nests under the existing /admin/providers/:id/parents Prefix
 * (D-225-safe — no new ingress leaf):
 *   POST   .../parents/:accountId/credentials/reveal   (manage) -> {email,password}
 *   POST   .../parents/:accountId/credentials          (manage) re-encrypt password
 *   POST   .../parents/:accountId/reprobe              (manage) probe-loop nudge
 *   POST   .../parents/:accountId/reset-quarantine     (manage) login_failed->active
 *   POST   .../parents/:accountId/sale-status          (manage) disable/enable
 *   DELETE .../parents/:accountId                      (manage soft / drain hard)
 *   POST   .../parents/export-csv                      (inspect) non-secret CSV
 *   POST   .../parents/bulk-reprobe                    (manage)
 *   POST   .../parents/bulk-take-off-sale              (manage)
 *   POST   .../parents/retry-problematic               (manage) server-picked
 *          login_failed retry — dry-run first, then {confirm:true} (2026-09-22)
 *
 * SECRET HYGIENE: the password is NEVER in the list, CSV, log or URL — only the
 * explicit, audited reveal endpoint returns it (in the response body). This
 * module never echoes a submitted password back into any value it returns.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from '../../api/client.js';
import type { LoginFailureReasonWire } from '../../i18n/login-failure-reasons.js';

/** The 11 mailbox-unit providers (mail.com/gmx are the SEPARATE alias family). */
export type MailboxUnitProviderId =
  | 'rambler'
  | 'aol'
  | 'webde'
  | 'libero'
  | 'tiscaliit'
  | 'tiscalicz'
  | 'sinavip'
  | 'sina'
  | 'gazetapl'
  | 'rediffmail'
  | 'tonline';

/** Lease state of the single unit alias behind a mailbox. */
export type MailboxLeaseState = 'available' | 'reserved' | 'leased';

/** A tone token compatible with the UI Badge component (kept UI-free here). */
export type MailboxTone = 'success' | 'warn' | 'danger' | 'neutral' | 'info';

/**
 * Raw mailbox row exactly as GET /site-usage returns it (post-extension). Every
 * field is optional so an older payload (before accountId/aliasId/leaseState
 * were added) still normalizes without throwing.
 */
export interface RawMailboxRow {
  readonly address: string;
  readonly accountId?: string;
  readonly aliasId?: string;
  readonly unitStatus?: string;
  readonly accountStatus?: string;
  readonly sessionState?: string;
  readonly leaseState?: MailboxLeaseState;
  readonly verified?: boolean;
  readonly hasCredential?: boolean;
  readonly consecutiveErrors?: number;
  readonly cooldownUntil?: string | null;
  readonly lastSeenAt?: string | null;
  readonly burnedSites?: readonly string[];
  readonly servedSites?: readonly string[];
  /**
   * 2026-09-22 — classified login-rejection reason. The server sends it ONLY
   * for accountStatus='login_failed' (the stored metadata is stale history on
   * recovered rows); null/absent otherwise.
   */
  readonly failureReason?: LoginFailureReasonWire | null;
  /** login_failed AND consecutive_errors >= 10 — the probe loop stopped retrying. */
  readonly sticky?: boolean;
}

/** The parts of the /site-usage read model the manager consumes. */
export interface MailboxUnitReadModel {
  readonly providerId: string;
  readonly mailboxes?: readonly RawMailboxRow[];
}

/** Normalized row the manager renders and acts on (every field present). */
export interface MailboxRow {
  readonly address: string;
  readonly accountId: string;
  readonly aliasId: string;
  /** Account status: active | login_failed | suspended | disabled. */
  readonly status: string;
  readonly sessionState: string;
  readonly leaseState: MailboxLeaseState;
  readonly consecutiveErrors: number;
  readonly cooldownUntil: string | null;
  readonly lastSeenAt: string | null;
  readonly verified: boolean;
  readonly hasCredential: boolean;
  readonly burnedSites: readonly string[];
  readonly servedSites: readonly string[];
  /** Only ever set on login_failed rows (see RawMailboxRow.failureReason). */
  readonly failureReason: LoginFailureReasonWire | null;
  /** login_failed with the 10-attempt limit reached (automatic retries stopped). */
  readonly sticky: boolean;
}

/** Probe loop's automatic-retry ceiling (v2_adapter candidate query: consecutive_errors < 10). */
export const STICKY_ERROR_THRESHOLD = 10;

function providerBase(providerId: string): string {
  return `/admin/providers/${encodeURIComponent(providerId)}`;
}

function parentBase(providerId: string, accountId: string): string {
  return `${providerBase(providerId)}/parents/${encodeURIComponent(accountId)}`;
}

/** Derive the lease badge from the extended field, falling back to unitStatus. */
function deriveLeaseState(raw: RawMailboxRow): MailboxLeaseState {
  if (raw.leaseState === 'leased' || raw.leaseState === 'reserved' || raw.leaseState === 'available') {
    return raw.leaseState;
  }
  if (raw.unitStatus === 'leased') return 'leased';
  if (raw.unitStatus === 'reserved') return 'reserved';
  return 'available';
}

/** Normalize a raw /site-usage mailbox row into the manager's model. */
export function toMailboxRow(raw: RawMailboxRow): MailboxRow {
  const status = raw.accountStatus ?? 'unknown';
  const consecutiveErrors = raw.consecutiveErrors ?? 0;
  const loginFailed = status === 'login_failed';
  return {
    address: raw.address,
    accountId: raw.accountId ?? '',
    aliasId: raw.aliasId ?? '',
    status,
    sessionState: raw.sessionState ?? 'unknown',
    leaseState: deriveLeaseState(raw),
    consecutiveErrors,
    cooldownUntil: raw.cooldownUntil ?? null,
    lastSeenAt: raw.lastSeenAt ?? null,
    verified: raw.verified ?? false,
    hasCredential: raw.hasCredential ?? false,
    burnedSites: raw.burnedSites ?? [],
    servedSites: raw.servedSites ?? [],
    // Defensive: never show a (stale) reason on a row that is not login_failed.
    failureReason: loginFailed ? raw.failureReason ?? null : null,
    // Older payloads (before `sticky`) fall back to the same predicate.
    sticky:
      typeof raw.sticky === 'boolean'
        ? loginFailed && raw.sticky
        : loginFailed && consecutiveErrors >= STICKY_ERROR_THRESHOLD,
  };
}

export function toMailboxRows(rows: readonly RawMailboxRow[] | undefined): MailboxRow[] {
  return (rows ?? []).map(toMailboxRow);
}

// ---------------------------------------------------------------------------
// Read model
// ---------------------------------------------------------------------------

export async function fetchMailboxUnits(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
): Promise<ApiResult<MailboxUnitReadModel>> {
  return callAdminApi<MailboxUnitReadModel>(options, { path: `${providerBase(providerId)}/site-usage` });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface RevealResult {
  readonly email: string;
  readonly password: string;
}

/** Explicit, audited password reveal. Returns the secret ONLY in the body. */
export async function revealPassword(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
): Promise<ApiResult<RevealResult>> {
  return liftErrorCode(
    await callAdminApi<RevealResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/credentials/reveal`,
    }),
  );
}

export interface UpdatePasswordInput {
  readonly password: string;
  /** AOL: `web=<web password>;phone=<phone>` (mirrors parseAolCredentialLine). */
  readonly extraSecret?: string;
}

export interface UpdatePasswordResult {
  readonly accountId: string;
  readonly accountHandle: string;
  readonly hasCredential: boolean;
  /**
   * 2026-09-22 — site-scoped providers: the saved password also queued one
   * fresh credential check (same nudge as "Yeniden dene"). Absent on older servers.
   */
  readonly reprobeQueued?: boolean;
}

export async function updatePassword(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  input: UpdatePasswordInput,
): Promise<ApiResult<UpdatePasswordResult>> {
  const body: Record<string, unknown> = { password: input.password };
  if (input.extraSecret !== undefined) body.extraSecret = input.extraSecret;
  return liftErrorCode(
    await callAdminApi<UpdatePasswordResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/credentials`,
      body,
    }),
  );
}

export interface ReprobeResult {
  readonly eligibleNextTick: boolean;
  readonly sessionState: string;
  /**
   * 2026-09-22 — the account had hit the 10-attempt limit; the nudge granted
   * it exactly one fresh probe. Absent on older servers.
   */
  readonly wasSticky?: boolean;
}

export async function reprobe(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
): Promise<ApiResult<ReprobeResult>> {
  return liftErrorCode(
    await callAdminApi<ReprobeResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/reprobe`,
    }),
  );
}

export interface ResetQuarantineResult {
  readonly accountId: string;
  readonly outcome: string;
}

export async function resetQuarantine(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  reason: string,
): Promise<ApiResult<ResetQuarantineResult>> {
  return liftErrorCode(
    await callAdminApi<ResetQuarantineResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/reset-quarantine`,
      body: { reason },
    }),
  );
}

export interface SaleStatusInput {
  readonly onSale: boolean;
  readonly reason: string;
}

export interface SaleStatusResult {
  readonly accountId: string;
  readonly onSale: boolean;
}

export async function setSaleStatus(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  input: SaleStatusInput,
): Promise<ApiResult<SaleStatusResult>> {
  return liftErrorCode(
    await callAdminApi<SaleStatusResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/sale-status`,
      body: { onSale: input.onSale, reason: input.reason },
    }),
  );
}

export type DeleteMode = 'soft' | 'hard';

export interface DeleteInput {
  readonly mode?: DeleteMode;
  readonly reason: string;
  /** Hard delete only: must equal the accountId (server also enforces). */
  readonly confirmationToken?: string;
}

export interface DeleteResult {
  readonly accountId: string;
  readonly outcome: string;
}

export async function deleteMailbox(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  input: DeleteInput,
): Promise<ApiResult<DeleteResult>> {
  const body: Record<string, unknown> = { reason: input.reason };
  if (input.mode !== undefined) body.mode = input.mode;
  if (input.confirmationToken !== undefined) body.confirmationToken = input.confirmationToken;
  return liftErrorCode(
    await callAdminApi<DeleteResult>(options, {
      method: 'DELETE',
      path: parentBase(providerId, accountId),
      body,
    }),
  );
}

export interface ExportCsvResult {
  readonly csvBase64: string;
  readonly rowCount: number;
}

/** Opt-in CSV of NON-SECRET columns only. Secrets are never exportable. */
export async function exportCsv(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountIds?: readonly string[],
): Promise<ApiResult<ExportCsvResult>> {
  const body: Record<string, unknown> = {};
  if (accountIds && accountIds.length > 0) body.accountIds = accountIds;
  return liftErrorCode(
    await callAdminApi<ExportCsvResult>(options, {
      method: 'POST',
      path: `${providerBase(providerId)}/parents/export-csv`,
      body,
    }),
  );
}

export interface BulkReprobeResult {
  readonly requested: number;
  readonly nudged: number;
  readonly skippedInFlight: number;
}

export async function bulkReprobe(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountIds: readonly string[],
): Promise<ApiResult<BulkReprobeResult>> {
  return liftErrorCode(
    await callAdminApi<BulkReprobeResult>(options, {
      method: 'POST',
      path: `${providerBase(providerId)}/parents/bulk-reprobe`,
      body: { accountIds },
    }),
  );
}

/** "Sorunluları tekrar dene" — dry-run preview (confirm !== true). */
export interface RetryProblematicDryRun {
  readonly dryRun: true;
  /** login_failed accounts with a credential and no live order that would be nudged. */
  readonly candidates: number;
  /** Of the candidates, how many sit at the 10-attempt limit. */
  readonly sticky: number;
  /** login_failed accounts skipped because their unit is reserved/leased. */
  readonly skippedInFlight: number;
  /** login_failed accounts without a stored credential (nothing to retry with). */
  readonly noCredential: number;
}

/** "Sorunluları tekrar dene" — applied (confirm === true). */
export interface RetryProblematicApplied {
  readonly dryRun: false;
  readonly requested: number;
  readonly nudged: number;
  readonly skippedInFlight: number;
}

export type RetryProblematicResult = RetryProblematicDryRun | RetryProblematicApplied;

export interface RetryProblematicInput {
  /** true applies the nudge; anything else is a dry run (server-enforced). */
  readonly confirm: boolean;
  /** Fresh probe attempts granted per account (server accepts 1..3, default 3). */
  readonly attempts?: number;
  readonly reason?: string;
}

/**
 * 2026-09-22 — server-picked bulk retry of login_failed mailboxes. The SERVER
 * chooses the candidates (login_failed + credential + not in a live order,
 * max 500); the client never sends ids. Call with confirm:false first.
 */
export async function retryProblematic(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  input: RetryProblematicInput,
): Promise<ApiResult<RetryProblematicResult>> {
  const body: Record<string, unknown> = { confirm: input.confirm };
  if (input.attempts !== undefined) body.attempts = input.attempts;
  if (input.reason !== undefined) body.reason = input.reason;
  return liftErrorCode(
    await callAdminApi<RetryProblematicResult>(options, {
      method: 'POST',
      path: `${providerBase(providerId)}/parents/retry-problematic`,
      body,
    }),
  );
}

export interface BulkTakeOffSaleResult {
  readonly requested: number;
  readonly disabled: number;
  readonly skippedInFlight: number;
}

export async function bulkTakeOffSale(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountIds: readonly string[],
  reason: string,
): Promise<ApiResult<BulkTakeOffSaleResult>> {
  return liftErrorCode(
    await callAdminApi<BulkTakeOffSaleResult>(options, {
      method: 'POST',
      path: `${providerBase(providerId)}/parents/bulk-take-off-sale`,
      body: { accountIds, reason },
    }),
  );
}

// ---------------------------------------------------------------------------
// Helpers (pure — unit-tested)
// ---------------------------------------------------------------------------

/** Envelope codes the server puts in `error` while the specific cause is in `detail`. */
const GENERIC_ERROR_CODES: ReadonlySet<string> = new Set([
  'conflict',
  'not_found',
  'bad_request',
  'service_unavailable',
]);

/**
 * The shared client lifts `body.reason` into `reason`; error bodies shaped
 * `{ error, detail }` would otherwise surface as the opaque `http_<status>`.
 * The server's real bodies are e.g. 409 {error:'conflict', detail:'mailbox_in_flight'}
 * and 503 {error:'service_unavailable', detail:'credential_vault_not_configured'},
 * so the specific cause lives in `detail`: prefer it when `error` is a generic
 * envelope code (or absent) or when `detail` is a known REASON_TEXT key;
 * otherwise lift `error` (e.g. 404 {error:'provider_not_supported',
 * detail:'site_scoped_rights_required'}).
 */
export function liftErrorCode<T>(res: ApiResult<T>): ApiResult<T> {
  if (res.kind !== 'error' || !res.reason.startsWith('http_')) return res;
  const error = res.body?.error;
  const detail = res.body?.detail;
  const errorCode = typeof error === 'string' && error.length > 0 ? error : null;
  if (typeof detail === 'string' && detail.length > 0) {
    if (errorCode === null || GENERIC_ERROR_CODES.has(errorCode) || hasReasonText(detail)) {
      return { ...res, reason: detail };
    }
  }
  return errorCode !== null ? { ...res, reason: errorCode } : res;
}

/**
 * Client-side pristine hint for the hard-delete affordance. The SERVER is
 * authoritative (it also checks reservations + inbox); this only decides whether
 * to SHOW the "Kalıcı sil" tab: never sold, never burned, currently available.
 */
export function isPristineRow(row: MailboxRow): boolean {
  return row.leaseState === 'available' && row.servedSites.length === 0 && row.burnedSites.length === 0;
}

export function leaseStateLabel(state: MailboxLeaseState): { readonly text: string; readonly tone: MailboxTone } {
  if (state === 'leased') return { text: 'Kirada', tone: 'info' };
  if (state === 'reserved') return { text: 'Rezerve', tone: 'warn' };
  return { text: 'Müsait', tone: 'success' };
}

export function statusLabel(status: string): { readonly text: string; readonly tone: MailboxTone } {
  switch (status) {
    case 'active':
      return { text: 'Aktif', tone: 'success' };
    case 'login_failed':
      return { text: 'Giriş başarısız', tone: 'danger' };
    case 'suspended':
      return { text: 'Askıda', tone: 'warn' };
    case 'disabled':
      return { text: 'Devre dışı', tone: 'neutral' };
    default:
      return { text: status, tone: 'neutral' };
  }
}

const REASON_TEXT: Readonly<Record<string, string>> = {
  provider_not_supported: 'Bu sağlayıcı için hesap yönetimi desteklenmiyor.',
  no_credential_on_account: 'Bu hesapta kayıtlı şifre yok — önce "Şifre değiştir" ile şifre girin.',
  mailbox_not_probeable:
    'Bu hesap doğrulama döngüsünde değil (devre dışı veya askıda), yeniden denenemez — devre dışıysa önce "Satışa aç" ile satışa açın; askıdaysa önce "Satıştan kaldır", ardından "Satışa aç".',
  account_not_found: 'Hesap bulunamadı (silinmiş olabilir) — listeyi yenileyin.',
  password_required: 'Şifre boş olamaz.',
  reason_required_min_4_chars: 'Gerekçe en az 4 karakter olmalı.',
  confirmation_token_must_equal_account_id: 'Onay kodu (hesap kimliği) eşleşmedi.',
  postgres_unavailable: 'Veritabanına ulaşılamadı — işlem yapılamadı, biraz sonra tekrar deneyin.',
  attempts_must_be_1_to_3: 'Deneme hakkı 1 ile 3 arasında olmalı.',
  credential_vault_not_configured:
    'Sunucuda kimlik kasası yapılandırılmamış — şifre saklanamadığı için işlem yapılamadı.',
  mailbox_in_flight:
    'Bu mailbox şu anda bir siparişte (rezerve/kirada) — canlı sipariş kapanmadan işlem yapılamaz.',
  mailbox_has_history_use_soft_delete:
    'Bu mailbox daha önce satılmış/kullanılmış — kalıcı silinemez, yumuşak sil kullanın.',
  not_quarantined: 'Bu hesap karantinada değil — çıkarılacak bir şey yok.',
  reprobe_in_flight: 'Doğrulanmış mailbox canlı bir teslimatta — yeniden denenmedi.',
  reason_too_short: 'Gerekçe en az 4 karakter olmalı.',
  invalid_reason: 'Gerekçe en az 4 karakter olmalı.',
  confirmation_required: 'Onay kodu (hesap kimliği) eşleşmedi.',
  invalid_confirmation: 'Onay kodu (hesap kimliği) eşleşmedi.',
  too_many_ids: 'Çok fazla mailbox seçildi — en fazla 500 satır işlenebilir.',
  secrets_not_exportable: 'Gizli sütunlar CSV’ye aktarılamaz.',
  include_secrets_forbidden: 'Gizli sütunlar CSV’ye aktarılamaz.',
  forbidden: 'Yetki yok: bu işlem için gerekli izin hesabınızda yok.',
  auth_required: 'Oturum gerekli — lütfen admin girişi yapın.',
  empty_response_body: 'Sunucu boş yanıt döndü.',
};

function hasReasonText(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(REASON_TEXT, code);
}

/**
 * Turkish operator text for an API / validation reason code. Only the code is
 * ever shown — never a submitted value (a password could be in it).
 */
export function reasonText(reason: string): string {
  const known = hasReasonText(reason) ? REASON_TEXT[reason] : undefined;
  if (known) return known;
  if (reason.startsWith('network:')) return 'Bağlantı hatası — sunucuya ulaşılamadı.';
  return `İşlem başarısız (${reason}).`;
}

/** Trigger a browser download of a base64 CSV (no secret ever passes here). */
export function downloadCsv(csvBase64: string, filename: string): void {
  const bytes = atob(csvBase64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  const blob = new Blob([buf], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
